/**
 * Hooks for showing translated content: the display text of a
 * `MultiLanguageString` and the current silo's language settings.
 *
 * Values arrive already resolved by the server's wire mappers, so these hooks
 * only pick which text to show and how to label the language.
 */

import { displayTextOf, type MultiLanguageString } from '@repo/shared/orpc/multi-language';
import { useSiloLanguage } from 'component/translation';
import { useSessionData, useSiloKey } from 'util/datastore';
import { isMultiLanguageSiloKey } from 'util/siloLanguages';
import { getDeploymentConfig } from 'util/util';

/**
 * The text to display for a `MultiLanguageString`, respecting the user's
 * "show original" toggle stored in session data at `['showOriginal', key]`.
 */
export function useDisplayText(value: MultiLanguageString | undefined, key: string): string {
    const isoLocale = useSiloLanguage();
    const showOriginal = useSessionData(['showOriginal', key]);
    if (value === undefined) return '';
    if (typeof value === 'string') return value;
    return showOriginal ? value.originalText : displayTextOf(value, isoLocale);
}

export function useIsMultiLanguageSilo(): boolean {
    return isMultiLanguageSiloKey(useSiloKey());
}

/**
 * Country code (e.g. `'CH'`) the current silo wants rendered as a prefix on
 * language flags, or `null` for no prefix. Driven by
 * `languageFlagCountryPrefix` in `psi.config.ts` so partner-specific behavior
 * stays out of core components.
 */
export function useLanguageFlagCountryPrefix(): 'CH' | null {
    const siloKey = useSiloKey();
    return getDeploymentConfig()?.siloConfig?.[siloKey]?.languageFlagCountryPrefix ?? null;
}
